import React, { useMemo } from "react";
import { ChartBarSquareIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { OverflowInfoCell } from "./OverflowInfoCell";
import { useVirtualizedRows } from "./hooks/useVirtualizedRows";

const LEAVE_HOURS_COLUMN = "İzin-Saat";

export const ReportsTab: React.FC<any> = ({
  reportMonth,
  setReportMonth,
  reportYear,
  setReportYear,
  reportView,
  setReportView,
  reportProjectRef,
  reportProjectNames,
  reportGroupColumns,
  isReportProjectOpen,
  setIsReportProjectOpen,
  setIsReportActivityOpen,
  setIsReportUserOpen,
  reportProjectSearch,
  setReportProjectSearch,
  filteredReportProjects,
  toggleReportProjectName,
  setReportProjectNames,
  reportActivityRef,
  reportActivityOptions,
  fetchActivities,
  isReportActivityOpen,
  reportActivitySearch,
  setReportActivitySearch,
  filteredReportActivities,
  reportActivityIds,
  toggleReportActivityId,
  setReportActivityIds,
  reportUserRef,
  reportUserOptions,
  fetchUsers,
  isReportUserOpen,
  reportUserSearch,
  setReportUserSearch,
  filteredReportUsers,
  reportUserIds,
  toggleReportUserId,
  setReportUserIds,
  getSelectionSummary,
  reportSearch,
  setReportSearch,
  exportReportToExcel,
  t,
  tableCardRef,
  tableCardStyle,
  visibleReportColumns,
  displayMonthlyReport,
  reportLoading,
  canViewCosts,
  reportMonthlyCostByUserId,
  formatCurrencyTr,
  reportSummaryMode,
  setReportSummaryMode,
  reportSummaryDisplay,
  reportCostDisplayValue,
}) => {
  const labels = {
    month: t("dashboard:managerReports.month"),
    year: t("dashboard:managerReports.year"),
    view: t("dashboard:managerReports.view"),
    viewProjectHours: t("dashboard:managerReports.viewProjectHours"),
    viewActivityHours: t("dashboard:managerReports.viewActivityHours"),
    viewUserSummary: t("dashboard:managerReports.viewUserSummary"),
    viewLeaveHours: t("dashboard:managerReports.viewLeaveHours"),
    projectFilter: t("dashboard:managerReports.projectFilter"),
    activityFilter: t("dashboard:managerReports.activityFilter"),
    staffFilter: t("dashboard:managerReports.staffFilter"),
    selectProject: t("dashboard:managerReports.selectProject"),
    selectActivity: t("dashboard:managerReports.selectActivity"),
    selectStaff: t("dashboard:managerReports.selectStaff"),
    searchProject: t("dashboard:managerReports.searchProject"),
    searchActivity: t("dashboard:managerReports.searchActivity"),
    searchStaff: t("dashboard:managerReports.searchStaff"),
    listLoading: t("dashboard:managerReports.listLoading"),
    selected: t("dashboard:managerReports.selected"),
    searchByStaffName: t("dashboard:managerReports.searchByStaffName"),
    excelDownload: t("dashboard:managerReports.excelDownload"),
    staff: t("dashboard:managerReports.staff"),
    search: t("common:search"),
    hour: t("dashboard:hours"),
    total: t("dashboard:managerReports.total"),
    required: t("dashboard:managerReports.required"),
    difference: t("dashboard:managerReports.difference"),
    leave: t("dashboard:managerReports.leave"),
    costTl: t("dashboard:managerReports.costTl"),
    preparing: t("dashboard:managerReports.preparing"),
    noDataInPeriod: t("dashboard:managerReports.noDataInPeriod"),
    average: t("dashboard:managerReports.average"),
  };
  const reportTableColumnCount =
    visibleReportColumns.length +
    1 +
    (reportView !== "leave-hours" ? 4 + (canViewCosts ? 1 : 0) : 0);
  const {
    scrollContainerRef,
    setScrollTop,
    virtualizationEnabled,
    startIndex: virtualStartIndex,
    endIndex: virtualEndIndex,
    topSpacerHeight,
    bottomSpacerHeight,
  } = useVirtualizedRows({
    itemCount: displayMonthlyReport.length,
    rowHeight: 48,
    overscan: 8,
    enabled: !reportLoading && displayMonthlyReport.length > 60,
    resetKey: `${reportMonth}-${reportYear}-${reportView}-${displayMonthlyReport.length}`,
  });
  const visibleMonthlyReport = useMemo(
    () =>
      virtualizationEnabled
        ? displayMonthlyReport.slice(virtualStartIndex, virtualEndIndex)
        : displayMonthlyReport,
    [
      displayMonthlyReport,
      virtualEndIndex,
      virtualStartIndex,
      virtualizationEnabled,
    ]
  );
  const hasOpenReportFilterDropdown =
    isReportProjectOpen || isReportActivityOpen || isReportUserOpen;

  return (
            <div className="space-y-0">
              {/* Report Filters */}
              <div
                className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 p-3 relative z-[60] overflow-visible"
              >
                <div
                  className={`flex items-end gap-1 min-w-max flex-nowrap ${
                    hasOpenReportFilterDropdown
                      ? "overflow-visible"
                      : "overflow-visible sm:overflow-x-auto sm:overflow-y-visible"
                  }`}
                >
                  <div className="shrink-0">
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      {labels.month}
                    </label>
                    <select
                      value={reportMonth}
                      onChange={(e) => setReportMonth(Number(e.target.value))}
                      className="block w-[106px] xl:w-[122px] h-8 rounded-lg border-2 border-gray-200 px-2 shadow-sm focus:border-green-500 focus:ring-green-500 text-[13px] py-1 font-medium transition-all"
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m: number) => (
                        <option key={m} value={m}>
                          {new Date(0, m - 1).toLocaleString("tr-TR", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="shrink-0">
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      {labels.year}
                    </label>
                    <input
                      type="number"
                      value={reportYear}
                      onChange={(e) => setReportYear(Number(e.target.value))}
                      className="block w-[66px] xl:w-[80px] h-8 rounded-lg border-2 border-gray-200 px-2 shadow-sm focus:border-green-500 focus:ring-green-500 text-[13px] py-1 font-medium transition-all"
                    />
                  </div>
                  <div className="shrink-0">
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      {labels.view}
                    </label>
                    <select
                      value={reportView}
                      onChange={(e) => setReportView(e.target.value)}
                      className="block w-[106px] xl:w-[122px] h-8 rounded-lg border-2 border-gray-200 px-2 shadow-sm focus:border-green-500 focus:ring-green-500 text-[13px] py-1 font-medium transition-all"
                    >
                      <option value="project">{labels.viewProjectHours}</option>
                      <option value="activity">{labels.viewActivityHours}</option>
                      <option value="user">{labels.viewUserSummary}</option>
                      <option value="leave-hours">{labels.viewLeaveHours}</option>
                    </select>
                  </div>
                  {reportView === "project" && (
                    <div
                      className="w-[145px] xl:w-[176px] min-w-[130px] relative shrink-0"
                      ref={reportProjectRef}
                    >
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        {labels.projectFilter}
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setIsReportProjectOpen((prev: boolean) => !prev);
                          setIsReportActivityOpen(false);
                          setIsReportUserOpen(false);
                        }}
                        className="w-full h-8 rounded-lg border-2 border-gray-200 bg-white shadow-sm px-2 py-1 text-left text-[13px] font-medium text-gray-700 flex items-center justify-between"
                      >
                        <span className="truncate">
                          {reportProjectNames.length > 0
                            ? `${reportProjectNames.slice(0, 2).join(", ")}${
                                reportProjectNames.length > 2
                                  ? ` +${reportProjectNames.length - 2}`
                                  : ""
                              }`
                            : labels.selectProject}
                        </span>
                        <span className="ml-3 text-xs text-gray-500">
                          {reportProjectNames.length > 0
                            ? `${reportProjectNames.length}/${reportGroupColumns.length}`
                            : reportGroupColumns.length}
                        </span>
                      </button>
                      {isReportProjectOpen && (
                        <div className="absolute left-0 right-0 z-50 mt-2 rounded-xl border border-gray-200 bg-white shadow-lg sm:right-auto sm:w-72">
                          <div className="px-3 py-2 border-b border-gray-100">
                            <input
                              type="text"
                              value={reportProjectSearch}
                              onChange={(e) =>
                                setReportProjectSearch(e.target.value)
                              }
                              placeholder={labels.searchProject}
                              className="w-full text-sm text-gray-700 focus:outline-none"
                            />
                          </div>
                          <div className="max-h-60 overflow-auto">
                            {reportGroupColumns.length === 0 ? (
                              <div className="px-3 py-3 text-sm text-gray-400">
                                {labels.listLoading}
                              </div>
                            ) : filteredReportProjects.length === 0 ? (
                              <div className="px-3 py-3 text-sm text-gray-400">
                                {t("common:noResults")}
                              </div>
                            ) : (
                              filteredReportProjects.map((projectName: string) => {
                                const isSelected =
                                  reportProjectNames.includes(projectName);
                                return (
                                  <label
                                    key={projectName}
                                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-green-50 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() =>
                                        toggleReportProjectName(projectName)
                                      }
                                      className="h-4 w-4 text-green-600 border-gray-300 rounded"
                                    />
                                    <span className="truncate">
                                      {projectName}
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                          <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
                            <span>
                              {reportProjectNames.length} {labels.selected}
                            </span>
                            {reportProjectNames.length > 0 && (
                              <button
                                onClick={() => setReportProjectNames([])}
                                className="text-green-600 hover:text-green-700 font-medium"
                              >
                                {t("common:clear")}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {reportView === "activity" && (
                    <div
                      className="w-[145px] xl:w-[176px] min-w-[130px] relative shrink-0"
                      ref={reportActivityRef}
                    >
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        {labels.activityFilter}
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          if (!reportActivityOptions.length) {
                            fetchActivities();
                          }
                          setIsReportActivityOpen((prev: boolean) => !prev);
                          setIsReportUserOpen(false);
                          setIsReportProjectOpen(false);
                        }}
                        className="w-full h-8 rounded-lg border-2 border-gray-200 bg-white shadow-sm px-2 py-1 text-left text-[13px] font-medium text-gray-700 flex items-center justify-between"
                      >
                        <span className="truncate">
                          {getSelectionSummary(
                            reportActivityOptions,
                            reportActivityIds,
                            labels.selectActivity,
                          )}
                        </span>
                        <span className="ml-3 text-xs text-gray-500">
                          {reportActivityIds.length > 0
                            ? `${reportActivityIds.length}/${reportActivityOptions.length}`
                            : reportActivityOptions.length}
                        </span>
                      </button>
                      {isReportActivityOpen && (
                        <div className="absolute left-0 right-0 z-50 mt-2 rounded-xl border border-gray-200 bg-white shadow-lg sm:right-auto sm:w-72">
                          <div className="px-3 py-2 border-b border-gray-100">
                            <input
                              type="text"
                              value={reportActivitySearch}
                              onChange={(e) =>
                                setReportActivitySearch(e.target.value)
                              }
                              placeholder={labels.searchActivity}
                              className="w-full text-sm text-gray-700 focus:outline-none"
                            />
                          </div>
                          <div className="max-h-60 overflow-auto">
                            {reportActivityOptions.length === 0 ? (
                              <div className="px-3 py-3 text-sm text-gray-400">
                                {labels.listLoading}
                              </div>
                            ) : filteredReportActivities.length === 0 ? (
                              <div className="px-3 py-3 text-sm text-gray-400">
                                {t("common:noResults")}
                              </div>
                            ) : (
                              filteredReportActivities.map((activity: any) => {
                                const isSelected = reportActivityIds.includes(
                                  activity.id,
                                );
                                return (
                                  <label
                                    key={activity.id}
                                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-green-50 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() =>
                                        toggleReportActivityId(activity.id)
                                      }
                                      className="h-4 w-4 text-green-600 border-gray-300 rounded"
                                    />
                                    <span className="truncate">
                                      {activity.label}
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                          <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
                            <span>
                              {reportActivityIds.length} {labels.selected}
                            </span>
                            {reportActivityIds.length > 0 && (
                              <button
                                onClick={() => setReportActivityIds([])}
                                className="text-green-600 hover:text-green-700 font-medium"
                              >
                                {t("common:clear")}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    className="w-[145px] xl:w-[176px] min-w-[130px] relative shrink-0"
                    ref={reportUserRef}
                  >
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      {labels.staffFilter}
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (!reportUserOptions.length) {
                          fetchUsers();
                        }
                        setIsReportUserOpen((prev: boolean) => !prev);
                        setIsReportActivityOpen(false);
                        setIsReportProjectOpen(false);
                      }}
                      className="w-full h-8 rounded-lg border-2 border-gray-200 bg-white shadow-sm px-2 py-1 text-left text-[13px] font-medium text-gray-700 flex items-center justify-between"
                    >
                      <span className="truncate">
                        {getSelectionSummary(
                          reportUserOptions,
                          reportUserIds,
                          labels.selectStaff,
                        )}
                      </span>
                      <span className="ml-3 text-xs text-gray-500">
                        {reportUserIds.length > 0
                          ? `${reportUserIds.length}/${reportUserOptions.length}`
                          : reportUserOptions.length}
                      </span>
                    </button>
                    {isReportUserOpen && (
                      <div className="absolute left-0 right-0 z-50 mt-2 rounded-xl border border-gray-200 bg-white shadow-lg sm:right-auto sm:w-72">
                        <div className="px-3 py-2 border-b border-gray-100">
                          <input
                            type="text"
                            value={reportUserSearch}
                            onChange={(e) =>
                              setReportUserSearch(e.target.value)
                            }
                            placeholder={labels.searchStaff}
                            className="w-full text-sm text-gray-700 focus:outline-none"
                          />
                        </div>
                        <div className="max-h-60 overflow-auto">
                          {reportUserOptions.length === 0 ? (
                            <div className="px-3 py-3 text-sm text-gray-400">
                              {labels.listLoading}
                            </div>
                          ) : filteredReportUsers.length === 0 ? (
                            <div className="px-3 py-3 text-sm text-gray-400">
                              {t("common:noResults")}
                            </div>
                          ) : (
                            filteredReportUsers.map((userItem: any) => {
                              const isSelected = reportUserIds.includes(
                                userItem.id,
                              );
                              return (
                                <label
                                  key={userItem.id}
                                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-green-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() =>
                                      toggleReportUserId(userItem.id)
                                    }
                                    className="h-4 w-4 text-green-600 border-gray-300 rounded"
                                  />
                                  <span className="truncate">
                                    {userItem.label}
                                  </span>
                                </label>
                              );
                            })
                          )}
                        </div>
                        <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
                          <span>
                            {reportUserIds.length} {labels.selected}
                          </span>
                          {reportUserIds.length > 0 && (
                            <button
                              onClick={() => setReportUserIds([])}
                              className="text-green-600 hover:text-green-700 font-medium"
                            >
                              {t("common:clear")}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="min-w-[220px] hidden shrink-0">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      {labels.activityFilter}
                    </label>
                    <div className="rounded-xl border-2 border-gray-200 shadow-sm bg-white overflow-hidden">
                      <div className="px-3 py-2 border-b border-gray-100">
                        <input
                          type="text"
                          value={reportActivitySearch}
                          onChange={(e) =>
                            setReportActivitySearch(e.target.value)
                          }
                          placeholder={labels.searchActivity}
                          className="w-full text-sm text-gray-700 focus:outline-none"
                        />
                      </div>
                      <div className="max-h-40 overflow-auto">
                        {filteredReportActivities.length === 0 ? (
                          <div className="px-3 py-3 text-sm text-gray-400">
                            {t("common:noResults")}
                          </div>
                        ) : (
                          filteredReportActivities.map((activity: any) => {
                            const isSelected = reportActivityIds.includes(
                              activity.id,
                            );
                            return (
                              <label
                                key={activity.id}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-green-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() =>
                                    toggleReportActivityId(activity.id)
                                  }
                                  className="h-4 w-4 text-green-600 border-gray-300 rounded"
                                />
                                <span className="truncate">
                                  {activity.label}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
                        <span>
                          {reportActivityIds.length} {labels.selected}
                        </span>
                        {reportActivityIds.length > 0 && (
                          <button
                            onClick={() => setReportActivityIds([])}
                            className="text-green-600 hover:text-green-700 font-medium"
                          >
                            {t("common:clear")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="min-w-[220px] hidden shrink-0">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      {labels.staffFilter}
                    </label>
                    <div className="rounded-xl border-2 border-gray-200 shadow-sm bg-white overflow-hidden">
                      <div className="px-3 py-2 border-b border-gray-100">
                        <input
                          type="text"
                          value={reportUserSearch}
                          onChange={(e) => setReportUserSearch(e.target.value)}
                          placeholder={labels.searchStaff}
                          className="w-full text-sm text-gray-700 focus:outline-none"
                        />
                      </div>
                      <div className="max-h-40 overflow-auto">
                        {filteredReportUsers.length === 0 ? (
                          <div className="px-3 py-3 text-sm text-gray-400">
                            {t("common:noResults")}
                          </div>
                        ) : (
                          filteredReportUsers.map((userItem: any) => {
                            const isSelected = reportUserIds.includes(
                              userItem.id,
                            );
                            return (
                              <label
                                key={userItem.id}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-green-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() =>
                                    toggleReportUserId(userItem.id)
                                  }
                                  className="h-4 w-4 text-green-600 border-gray-300 rounded"
                                />
                                <span className="truncate">
                                  {userItem.label}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
                        <span>
                          {reportUserIds.length} {labels.selected}
                        </span>
                        {reportUserIds.length > 0 && (
                          <button
                            onClick={() => setReportUserIds([])}
                            className="text-green-600 hover:text-green-700 font-medium"
                          >
                            {t("common:clear")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-[138px] xl:w-[170px] min-w-[124px] shrink-0">
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      {labels.search}
                    </label>
                    <input
                      type="text"
                      value={reportSearch}
                      onChange={(e) => setReportSearch(e.target.value)}
                      placeholder={labels.searchByStaffName}
                      className="block w-full h-8 rounded-lg border-2 border-gray-200 px-2 shadow-sm focus:border-green-500 focus:ring-green-500 text-[13px] py-1 font-medium transition-all"
                    />
                  </div>
                  <div className="ml-auto flex gap-1 shrink-0">
                    <button
                      onClick={() => {
                        setReportActivityIds([]);
                        setReportUserIds([]);
                        setReportProjectNames([]);
                        setReportSearch("");
                        setReportActivitySearch("");
                        setReportUserSearch("");
                        setReportProjectSearch("");
                      }}
                      className="inline-flex items-center h-8 px-2.5 py-1.5 text-[13px] font-bold rounded-lg shadow-md text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-all"
                    >
                      {t("common:clear")}
                    </button>
                    <button
                      onClick={exportReportToExcel}
                      className="inline-flex items-center h-8 px-2.5 py-1.5 border border-transparent text-[13px] font-bold rounded-lg shadow-md text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transform transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
                    >
                      <DocumentTextIcon className="w-4 h-4 xl:mr-1.5" />
                      <span className="hidden 2xl:inline">{labels.excelDownload}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Matrix Table */}
              <div
                ref={tableCardRef}
                className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 overflow-hidden flex flex-col"
                style={tableCardStyle}
              >
                <div
                  ref={scrollContainerRef}
                  onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
                  className="overflow-auto min-h-0 flex-1"
                >
                  <div className="space-y-3 p-3 md:hidden">
                    {reportLoading ? (
                      <div className="flex flex-col items-center justify-center py-8">
                        <div className="mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-green-600" />
                        <span className="text-sm font-medium text-gray-500">
                          {labels.preparing}
                        </span>
                      </div>
                    ) : displayMonthlyReport.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center">
                        <ChartBarSquareIcon className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                        <p className="text-sm font-medium text-gray-500">
                          {labels.noDataInPeriod}
                        </p>
                      </div>
                    ) : (
                      visibleMonthlyReport.map((row: any) => {
                        const userId = Number(row.userId);
                        const userCost = Number.isFinite(userId)
                          ? reportMonthlyCostByUserId.get(userId) ?? 0
                          : 0;
                        const previewColumns = visibleReportColumns.slice(0, 4);
                        const hiddenColumnCount = Math.max(
                          visibleReportColumns.length - previewColumns.length,
                          0
                        );

                        return (
                          <div
                            key={`mobile-report-${row.userId}`}
                            className="rounded-lg border border-green-100 bg-white px-3 py-2 shadow-sm"
                          >
                            <div className="flex items-start gap-2">
                              <span className="mt-0.5 flex h-7 min-w-7 items-center justify-center rounded-md bg-green-50 text-[11px] font-bold text-green-700">
                                {String(row.userName || "-").slice(0, 2)}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex min-w-0 items-center gap-2">
                                  <p className="truncate text-[13px] font-bold text-gray-900">
                                    {row.userName}
                                  </p>
                                  {reportView !== "leave-hours" && (
                                    <span className="shrink-0 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-bold text-green-700">
                                      {Number(row.totalHours ?? 0).toFixed(2)}{" "}
                                      {labels.hour}
                                    </span>
                                  )}
                                </div>
                                <p className="mt-0.5 truncate text-[11px] font-medium text-gray-500">
                                  {reportView === "leave-hours"
                                    ? labels.viewLeaveHours
                                    : labels.view}
                                </p>
                              </div>
                            </div>

                            {previewColumns.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {previewColumns.map((columnName: string) => (
                                  <span
                                    key={`mobile-report-${row.userId}-${columnName}`}
                                    className="max-w-full truncate rounded-md bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700"
                                  >
                                    {columnName === LEAVE_HOURS_COLUMN
                                      ? labels.viewLeaveHours
                                      : columnName}
                                    :{" "}
                                    {row.projects?.[columnName]?.toFixed(2) ||
                                      "-"}{" "}
                                    {labels.hour}
                                  </span>
                                ))}
                                {hiddenColumnCount > 0 && (
                                  <span className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
                                    +{hiddenColumnCount} {labels.selected}
                                  </span>
                                )}
                              </div>
                            )}

                            {reportView !== "leave-hours" && (
                              <div className="mt-2 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] font-semibold text-slate-600">
                                <span>
                                  {labels.required}:{" "}
                                  {Number(row.requiredHours ?? 0).toFixed(2)}
                                </span>
                                <span
                                  className={
                                    row.difference < 0
                                      ? "text-red-700"
                                      : "text-green-700"
                                  }
                                >
                                  {labels.difference}:{" "}
                                  {row.difference > 0 ? "+" : ""}
                                  {Number(row.difference ?? 0).toFixed(2)}
                                </span>
                                <span>
                                  {labels.leave}:{" "}
                                  {Number(row.leaveHours ?? 0).toFixed(2)}
                                </span>
                                {canViewCosts && (
                                  <span className="text-emerald-700">
                                    {labels.costTl}: {formatCurrencyTr(userCost)}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  <table className="hidden min-w-full divide-y divide-gray-200 md:table">
                    <thead className="bg-gradient-to-r from-green-50 to-emerald-50 sticky top-0 z-30 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-green-800 uppercase tracking-wider sticky top-0 left-0 z-40 bg-gradient-to-r from-green-50 to-emerald-50 shadow-sm border-r border-gray-200">
                          {labels.staff}
                        </th>
                        {/* Dynamic Project Headers */}
                        {visibleReportColumns.map((p: string) => (
                          <th
                            key={p}
                            className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider min-w-[140px]"
                          >
                            <div className="flex flex-col items-end">
                              <div className="max-w-[180px]">
                                <OverflowInfoCell
                                  value={
                                    p === LEAVE_HOURS_COLUMN
                                      ? labels.viewLeaveHours.toUpperCase()
                                      : p
                                  }
                                  title={
                                    p === LEAVE_HOURS_COLUMN
                                      ? labels.viewLeaveHours
                                      : p
                                  }
                                  threshold={18}
                                  cellClassName="justify-end"
                                  textClassName="text-right"
                                />
                              </div>
                              <span className="text-[10px] font-normal text-gray-400 mt-0.5">
                                ({labels.hour})
                              </span>
                            </div>
                          </th>
                        ))}
                        {reportView !== "leave-hours" && (
                          <>
                            <th className="px-6 py-4 text-right text-xs font-bold text-blue-800 uppercase tracking-wider bg-blue-50/50">
                              {labels.total}
                            </th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-orange-800 uppercase tracking-wider bg-orange-50/50">
                              {labels.required}
                            </th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-gray-800 uppercase tracking-wider bg-gray-100/50">
                              {labels.difference}
                            </th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-red-800 uppercase tracking-wider bg-red-50/50">
                              {labels.leave}
                            </th>
                            {canViewCosts && (
                              <th className="px-6 py-4 text-right text-xs font-bold text-emerald-800 uppercase tracking-wider bg-emerald-50/50">
                                {labels.costTl}
                              </th>
                            )}
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {reportLoading ? (
                        <tr>
                          <td colSpan={100} className="p-12">
                            <div className="flex justify-center flex-col items-center">
                              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mb-4"></div>
                              <span className="text-gray-500 font-medium">
                                {labels.preparing}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : displayMonthlyReport.length === 0 ? (
                        <tr>
                          <td
                            colSpan={100}
                            className="p-12 text-center text-gray-500"
                          >
                            <div className="flex flex-col items-center">
                              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <ChartBarSquareIcon className="w-8 h-8 text-gray-400" />
                              </div>
                              <p className="font-medium">
                                {labels.noDataInPeriod}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <>
                          {virtualizationEnabled && topSpacerHeight > 0 && (
                            <tr>
                              <td
                                colSpan={reportTableColumnCount}
                                style={{ height: `${topSpacerHeight}px` }}
                                className="p-0 border-0"
                              />
                            </tr>
                          )}
                          {visibleMonthlyReport.map((row: any) => (
                          <tr
                            key={row.userId}
                            className="group hover:bg-green-50/30 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 text-xs font-bold text-gray-900 sticky left-0 bg-white shadow-sm border-r border-gray-100 group-hover:bg-green-50/30 max-w-[220px]">
                              <OverflowInfoCell
                                value={row.userName}
                                fullValue={row.userName}
                                title={labels.staff}
                                threshold={24}
                              />
                            </td>
                            {/* Project Cells */}
                            {visibleReportColumns.map((p: string) => (
                              <td
                                key={p}
                                className="px-6 py-4 whitespace-nowrap text-right text-xs text-gray-600 font-medium"
                              >
                                {row.projects?.[p]?.toFixed(2) || "-"}
                              </td>
                            ))}
                            {reportView !== "leave-hours" && (
                              <>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-bold text-blue-600 bg-blue-50/30 border-l border-blue-100">
                                  {row.totalHours?.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-bold text-orange-600 bg-orange-50/30 border-l border-orange-100">
                                  {Number(row.requiredHours ?? 0).toFixed(2)}
                                </td>
                                <td
                                  className={`px-6 py-4 whitespace-nowrap text-right text-xs font-bold bg-gray-50/50 border-l border-gray-100 ${
                                    row.difference < 0
                                      ? "text-red-600"
                                      : "text-green-600"
                                  }`}
                                >
                                  {row.difference > 0 ? "+" : ""}
                                  {row.difference?.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-bold text-red-700 bg-red-50/30 border-l border-red-100">
                                  {Number(row.leaveHours ?? 0).toFixed(2)}
                                </td>
                                {canViewCosts && (
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-bold text-emerald-700 bg-emerald-50/30 border-l border-emerald-100">
                                    {(() => {
                                      const userId = Number(row.userId);
                                      const userCost = Number.isFinite(userId)
                                        ? (reportMonthlyCostByUserId.get(
                                            userId,
                                          ) ?? 0)
                                        : 0;
                                      return formatCurrencyTr(userCost);
                                    })()}
                                  </td>
                                )}
                              </>
                            )}
                          </tr>
                          ))}
                          {virtualizationEnabled && bottomSpacerHeight > 0 && (
                            <tr>
                              <td
                                colSpan={reportTableColumnCount}
                                style={{ height: `${bottomSpacerHeight}px` }}
                                className="p-0 border-0"
                              />
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                    {!reportLoading && displayMonthlyReport.length > 0 && (
                      <tfoot className="sticky bottom-0 z-10 bg-white">
                        <tr className="bg-gray-50/90 backdrop-blur-sm border-t border-gray-200">
                          <td className="px-6 py-4 whitespace-nowrap text-xs font-extrabold text-gray-900 sticky left-0 z-30 bg-gray-50/90 shadow-sm border-r border-gray-200">
                            <div className="flex items-center gap-3">
                              <div className="inline-flex flex-none rounded-md border border-gray-300 overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => setReportSummaryMode("total")}
                                  className={`px-2 py-0.5 text-[10px] font-semibold shrink-0 whitespace-nowrap ${
                                    reportSummaryMode === "total"
                                      ? "bg-gray-800 text-white"
                                      : "bg-white text-gray-700 hover:bg-gray-100"
                                  }`}
                                >
                                  {labels.total}
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setReportSummaryMode("average")
                                  }
                                  className={`px-2 py-0.5 text-[10px] font-semibold border-l border-gray-300 shrink-0 whitespace-nowrap ${
                                    reportSummaryMode === "average"
                                      ? "bg-gray-800 text-white"
                                      : "bg-white text-gray-700 hover:bg-gray-100"
                                  }`}
                                >
                                  {labels.average}
                                </button>
                              </div>
                            </div>
                          </td>
                          {visibleReportColumns.map((p: string) => (
                            <td
                              key={p}
                              className="px-6 py-4 whitespace-nowrap text-right text-xs font-bold text-gray-700"
                            >
                              {(
                                reportSummaryDisplay.groupValues[p] ?? 0
                              ).toFixed(2)}
                            </td>
                          ))}
                          {reportView !== "leave-hours" && (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-extrabold text-blue-700 bg-blue-50/40 border-l border-blue-100">
                                {reportSummaryDisplay.totalHours.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-extrabold text-orange-700 bg-orange-50/40 border-l border-orange-100">
                                {reportSummaryDisplay.requiredHours.toFixed(2)}
                              </td>
                              <td
                                className={`px-6 py-4 whitespace-nowrap text-right text-xs font-extrabold bg-gray-100/40 border-l border-gray-200 ${
                                  reportSummaryDisplay.difference < 0
                                    ? "text-red-600"
                                    : "text-green-700"
                                }`}
                              >
                                {reportSummaryDisplay.difference > 0 ? "+" : ""}
                                {reportSummaryDisplay.difference.toFixed(2)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-extrabold text-red-800 bg-red-50/40 border-l border-red-100">
                                {reportSummaryDisplay.leaveHours.toFixed(2)}
                              </td>
                              {canViewCosts && (
                                <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-extrabold text-emerald-800 bg-emerald-50/40 border-l border-emerald-100">
                                  {formatCurrencyTr(reportCostDisplayValue)}
                                </td>
                              )}
                            </>
                          )}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </div>

  );
};
